import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const payuService = {
  /**
   * Inicializa un pago con PayU
   * @param {number} planId - ID del plan
   * @param {string} cicloFacturacion - Ciclo de facturación ('mensual' o 'anual')
   * @param {number} originalAmount - Monto original del plan
   * @param {number} finalAmount - Monto final después de descuentos
   * @param {number} cuponId - ID del cupón aplicado (opcional)
   * @param {number} saldoAplicado - Saldo aplicado (opcional)
   * @returns {Promise<Object>} Respuesta de la API
   */
  initializePayment: async (planId, cicloFacturacion, originalAmount, finalAmount = null, cuponId = null, saldoAplicado = 0) => {
    try {
      // Ensure amounts are numbers and properly formatted
      const originalAmountNumber = parseFloat(originalAmount);
      const finalAmountNumber = finalAmount !== null ? parseFloat(finalAmount) : originalAmountNumber;
      const saldoAplicadoNumber = parseFloat(saldoAplicado) || 0;
      
      if (isNaN(originalAmountNumber)) {
        throw new Error('El monto original debe ser un número válido');
      }
      
      if (isNaN(finalAmountNumber)) {
        throw new Error('El monto final debe ser un número válido');
      }
      
      console.log('Enviando solicitud de pago:', { 
        planId, 
        cicloFacturacion, 
        originalAmount: originalAmountNumber,
        finalAmount: finalAmountNumber,
        cuponId,
        saldoAplicado: saldoAplicadoNumber
      });
      
      // Make sure we're sending the exact field names the backend expects
      const response = await axios.post(`${API_URL}/api/pagos/payu/initialize`, {
        planId: parseInt(planId, 10),
        cicloFacturacion: cicloFacturacion,
        originalAmount: originalAmountNumber,
        finalAmount: finalAmountNumber,
        cuponId: cuponId ? parseInt(cuponId, 10) : null,
        saldoAplicado: saldoAplicadoNumber
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        validateStatus: function (status) {
          return status < 500;
        }
      });
      
      // Handle non-200 responses explicitly
      if (response.status !== 200) {
        console.error('PayU API error:', response.data);
        throw new Error(response.data.error || response.data.details || 'Error en el servicio de pago');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error initializing PayU payment:', error);
      
      if (error.response) {
        console.error('PayU API response error data:', error.response.data);
        console.error('PayU API response status:', error.response.status);
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error en la respuesta del servidor de pagos');
      } else if (error.request) {
        console.error('No response received from PayU API');
        throw new Error('No se recibió respuesta del servidor de pagos');
      } else {
        throw error;
      }
    }
  },
  
  /**
   * Verifica el estado de un pago
   * @param {string} transactionId - ID de la transacción
   * @returns {Promise<Object>} Estado del pago
   */
  verifyPayment: async (transactionId) => {
    try {
      const response = await axios.get(`${API_URL}/api/pagos/payu/verify/${transactionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying payment:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al verificar el pago');
      }
      throw error;
    }
  },
  
  /**
   * Obtiene los métodos de pago guardados del usuario
   * @returns {Promise<Object>} Métodos de pago guardados
   */
  getSavedPaymentMethods: async () => {
    try {
      console.log('Obteniendo métodos de pago guardados...');
      
      const response = await axios.get(`${API_URL}/api/pagos/methods`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Métodos de pago obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching saved payment methods:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al obtener métodos de pago');
      }
      throw error;
    }
  },
  
  /**
   * Guarda un método de pago después de validarlo
   * @param {Object} paymentData - Datos del método de pago
   * @param {string} paymentData.cardNumber - Número de tarjeta
   * @param {string} paymentData.cardHolder - Titular de la tarjeta
   * @param {string} paymentData.expiryMonth - Mes de expiración
   * @param {string} paymentData.expiryYear - Año de expiración
   * @param {string} paymentData.cvv - Código de seguridad
   * @param {string} paymentData.docNumber - Número de documento (opcional)
   * @returns {Promise<Object>} Resultado de la operación
   */
  savePaymentMethod: async (paymentData) => {
    try {
      console.log('Guardando método de pago:', {
        cardHolder: paymentData.cardHolder,
        hasCardNumber: !!paymentData.cardNumber,
        expiryMonth: paymentData.expiryMonth,
        expiryYear: paymentData.expiryYear,
        hasCvv: !!paymentData.cvv,
        hasDocNumber: !!paymentData.docNumber
      });
      
      // Validaciones en el frontend antes de enviar
      if (!paymentData.cardNumber || !paymentData.cardHolder || 
          !paymentData.expiryMonth || !paymentData.expiryYear || !paymentData.cvv) {
        throw new Error('Todos los campos de la tarjeta son requeridos');
      }
      
      // Validar formato básico del número de tarjeta
      const cleanCardNumber = paymentData.cardNumber.replace(/\s/g, '');
      if (!/^\d{13,19}$/.test(cleanCardNumber)) {
        throw new Error('Número de tarjeta inválido');
      }
      
      // Validar mes de expiración
      const month = parseInt(paymentData.expiryMonth);
      if (isNaN(month) || month < 1 || month > 12) {
        throw new Error('Mes de expiración inválido');
      }
      
      // Validar año de expiración
      const currentYear = new Date().getFullYear() % 100;
      const expiryYear = parseInt(paymentData.expiryYear);
      if (isNaN(expiryYear) || expiryYear < currentYear) {
        throw new Error('La tarjeta ha expirado');
      }
      
      // Validar CVV
      if (!/^\d{3,4}$/.test(paymentData.cvv)) {
        throw new Error('CVV inválido (debe tener 3 o 4 dígitos)');
      }
      
      const response = await axios.post(`${API_URL}/api/pagos/methods/save`, paymentData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Método de pago guardado exitosamente:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error saving payment method:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al guardar método de pago');
      }
      throw error;
    }
  },
  
  /**
   * Elimina un método de pago guardado
   * @param {string|number} methodId - ID del método de pago
   * @returns {Promise<Object>} Resultado de la operación
   */
  deletePaymentMethod: async (methodId) => {
    try {
      console.log('Eliminando método de pago:', methodId);
      
      if (!methodId) {
        throw new Error('ID del método de pago requerido');
      }
      
      const response = await axios.delete(`${API_URL}/api/pagos/methods/${methodId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Método de pago eliminado:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting payment method:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al eliminar método de pago');
      }
      throw error;
    }
  },
  
  /**
   * Establece un método de pago como principal
   * @param {string|number} methodId - ID del método de pago
   * @returns {Promise<Object>} Resultado de la operación
   */
  setPrimaryPaymentMethod: async (methodId) => {
    try {
      console.log('Estableciendo método de pago principal:', methodId);
      
      if (!methodId) {
        throw new Error('ID del método de pago requerido');
      }
      
      const response = await axios.post(`${API_URL}/api/pagos/methods/primary`, 
        { methodId: parseInt(methodId, 10) }, 
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Método principal establecido:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error setting primary payment method:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al establecer método principal');
      }
      throw error;
    }
  },
  
  /**
   * Obtiene el historial de pagos del usuario
   * @returns {Promise<Object>} Historial de pagos
   */
  getPaymentHistory: async () => {
    try {
      console.log('Obteniendo historial de pagos...');
      
      const response = await axios.get(`${API_URL}/api/pagos/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Historial de pagos obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al obtener historial de pagos');
      }
      throw error;
    }
  },
  
  /**
   * Cancela pagos recurrentes del usuario
   * @returns {Promise<Object>} Resultado de la operación
   */
  cancelRecurringPayments: async () => {
    try {
      console.log('Cancelando pagos recurrentes...');
      
      const response = await axios.post(`${API_URL}/api/pagos/recurring/cancel`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Pagos recurrentes cancelados:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error canceling recurring payments:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al cancelar pagos recurrentes');
      }
      throw error;
    }
  },
  
  /**
   * Obtiene los detalles de una transacción específica
   * @param {string|number} transactionId - ID de la transacción
   * @returns {Promise<Object>} Detalles de la transacción
   */
  getTransactionDetails: async (transactionId) => {
    try {
      console.log('Obteniendo detalles de transacción:', transactionId);
      
      if (!transactionId) {
        throw new Error('ID de transacción requerido');
      }
      
      const response = await axios.get(`${API_URL}/api/pagos/transaction/${transactionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Detalles de transacción obtenidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al obtener detalles de la transacción');
      }
      throw error;
    }
  },
  
  /**
   * Reintenta un pago fallido
   * @param {string|number} transactionId - ID de la transacción original
   * @returns {Promise<Object>} Resultado del reintento
   */
  retryPayment: async (transactionId) => {
    try {
      console.log('Reintentando pago:', transactionId);
      
      if (!transactionId) {
        throw new Error('ID de transacción requerido');
      }
      
      const response = await axios.post(`${API_URL}/api/pagos/retry-payment`, 
        { transactionId: parseInt(transactionId, 10) }, 
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Pago reintentado:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error retrying payment:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.details || 'Error al reintentar el pago');
      }
      throw error;
    }
  },
  
  /**
   * Función de utilidad para formatear número de tarjeta
   * @param {string} cardNumber - Número de tarjeta sin formato
   * @returns {string} Número de tarjeta formateado
   */
  formatCardNumber: (cardNumber) => {
    if (!cardNumber) return '';
    
    // Remover caracteres no numéricos
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    // Limitar a 19 dígitos máximo
    const limitedNumber = cleanNumber.slice(0, 19);
    
    // Formatear con espacios cada 4 dígitos
    return limitedNumber.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  },
  
  /**
   * Función de utilidad para detectar el tipo de tarjeta
   * @param {string} cardNumber - Número de tarjeta
   * @returns {string} Tipo de tarjeta
   */
  getCardType: (cardNumber) => {
    if (!cardNumber) return 'unknown';
    
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    if (cleanNumber.startsWith('4')) return 'visa';
    if (cleanNumber.startsWith('5') || cleanNumber.startsWith('2')) return 'mastercard';
    if (cleanNumber.startsWith('3')) return 'amex';
    if (cleanNumber.startsWith('6')) return 'discover';
    
    return 'other';
  },
  
  /**
   * Función de utilidad para validar número de tarjeta usando algoritmo de Luhn
   * @param {string} cardNumber - Número de tarjeta
   * @returns {boolean} True si es válido
   */
  validateCardNumber: (cardNumber) => {
    if (!cardNumber) return false;
    
    const cleanNumber = cardNumber.replace(/\D/g, '');
    
    if (cleanNumber.length < 13 || cleanNumber.length > 19) {
      return false;
    }
    
    // Algoritmo de Luhn
    let sum = 0;
    let isEven = false;
    
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanNumber.charAt(i), 10);
      
      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }
      
      sum += digit;
      isEven = !isEven;
    }
    
    return sum % 10 === 0;
  }
};

export default payuService;